/**
 * Pre-context top-row observation buffer — Push 11 / G-Max row-1 retention
 * (2026-06-22).
 *
 * THE PROBLEM (telemetry 2026-06-21, run after #474). When the user opens the
 * Pokédex at the TOP of a curated page and then scrolls before the scanner has
 * locked a stable variant context, the top rows are read by OCR while the
 * variant is still unknown / unstable — and then discarded. The device trace
 * shows it concretely for Kanto G-Max #003/#006/#009:
 *
 *   - Frames ~85 OCR cleanly read the top G-Max rows ([5,6,7,8,9,…] incl. 6)
 *     but the active pill still resolved as `shiny` (the user had not settled on
 *     the G-Max tab yet), so those reads were attributed to shiny and dropped.
 *   - By frame 115 the variant finally locked to `gmax` — but the user had
 *     already scrolled past row 1, so only rows r2 [25,52] and r3 [68] were ever
 *     observed under gmax. #003/#006/#009 were NEVER re-observed and never
 *     committed.
 *
 * The per-frame adapter and the bounded scan-context tracker
 * ([[createScanContextTracker]]) both have the cross-frame memory needed to
 * decide WHICH dex we are on — but neither retains the actual top-row dex
 * NUMBERS that were read just before the lock. Those numbers are gone by the
 * time the context says "this is the Kanto G-Max page".
 *
 * THE FIX. A tiny, pure, side-effect-free buffer that:
 *
 *   - RETAINS a short-lived snapshot of rows that OCR read while the context was
 *     still unknown / variant-unstable, TAGGED with the provisional variant they
 *     were read under and the frame index they were seen at. Only rows with a
 *     real provisional variant + at least one parsed id are retained — a bare
 *     "some dex page" frame buffers nothing.
 *
 *   - PROMOTES the buffered rows EXACTLY ONCE, only when a context lock arrives
 *     whose variant MATCHES the variant the rows were buffered under, and only
 *     for rows seen within [[PRE_CONTEXT_TTL_FRAMES]] of the lock. Promotion
 *     hands the rows back to the caller as ordinary observations to be routed
 *     under the locked variant — they then pass through EVERY unchanged write
 *     gate (membership, sprite, slot range, two-frame corroboration, cross-check,
 *     overlay-text). Promotion is a routing hint, never a write.
 *
 *   - EXPIRES rows quickly. A buffered row older than the TTL (in frames) is
 *     dropped on the next observe/promote. The buffer can bridge "user started at
 *     the top, scrolled, then the variant locked a second later" — never an
 *     indefinite stale carry.
 *
 *   - RESETS on a variant SWITCH. If the locked/observed variant disagrees with
 *     the buffered variant, the buffer is cleared: rows read under the OLD
 *     provisional variant must NEVER be promoted into a DIFFERENT dex.
 *
 * INVARIANTS (write accuracy is non-negotiable):
 *   - This module decides only WHICH previously-read rows a just-locked variant
 *     may RE-OBSERVE. It never applies an entry, never relaxes a gate, never
 *     fabricates a number it did not see. Every promoted row re-enters the
 *     session's ingest path and clears the same gates a live frame would.
 *   - Pure + framework-agnostic so retain / promote / expire / reset is
 *     unit-tested deterministically.
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/**
 * How many frames a buffered pre-context row survives. Sized to bridge the gap
 * the telemetry showed between a clean top-row read (~frame 85) and the variant
 * lock (~frame 115) — ~30 frames at the 1 Hz broadcast cadence is ~30s, longer
 * than a "open at top, scroll, settle on the tab" gesture but far short of an
 * indefinite carry. Rows older than this at promote/observe time are dropped.
 */
export const PRE_CONTEXT_TTL_FRAMES = 30;

/**
 * Maximum distinct rows the buffer holds at once. A curated page shows 4 rows;
 * we keep a little headroom for a partial-scroll overlap and cap it so a long
 * pre-lock dwell cannot grow the buffer without bound. When full, the OLDEST
 * row is evicted before a new one is retained.
 */
export const PRE_CONTEXT_MAX_ROWS = 8;

/** A single buffered row: the dex ids read for one row band, pre-lock. */
export interface PreContextRow {
  /** Stable row-band token (from `rowKeyForYPct`), e.g. "r0". */
  rowKey: string;
  /** Distinct dex ids reliably read for this row before the context locked. */
  ids: number[];
  /**
   * Per-id approximate cell y% (0-1), parallel to `ids`, so a promoted row can
   * carry the overlay-fence coordinate each id was seen at. Empty when the
   * caller had no yPct (older callers / tests) — promotion still works, the
   * downstream overlay fence simply treats a missing yPct as un-fenced.
   */
  yPcts: number[];
  /** Frame index the row was last (re)observed at, for TTL expiry. */
  frameIndex: number;
}

/** A row handed back to the caller to re-observe under the locked variant. */
export interface PromotedRow {
  rowKey: string;
  ids: number[];
  yPcts: number[];
}

export type PreContextEvent =
  /** A row was retained into the buffer this frame (pre-context observed). */
  | "observed"
  /** Buffered rows were promoted to the caller (pre-context promoted). */
  | "promoted"
  /** One or more buffered rows aged out past the TTL (pre-context expired). */
  | "expired"
  /** A variant switch cleared the buffer (rows under the old variant dropped). */
  | "variant_switch_reset"
  /** Nothing happened this frame. */
  | "idle";

export interface PreContextRetainResult {
  event: Extract<PreContextEvent, "observed" | "variant_switch_reset" | "idle">;
  /** Rows currently buffered after this retain (count, for telemetry). */
  buffered: number;
  /** Rows that aged out as part of this retain (for the expired marker). */
  expired: number;
}

export interface PreContextPromoteResult {
  event: Extract<PreContextEvent, "promoted" | "variant_switch_reset" | "idle">;
  /** Rows to re-observe under the locked variant. Empty unless event=promoted. */
  rows: PromotedRow[];
  /** Rows that aged out before promotion (for the expired marker). */
  expired: number;
}

/**
 * Create a stateful, frame-bounded pre-context row buffer. Feed each pre-lock
 * frame's read rows via `retain`, and call `promote` when a context lock lands.
 *
 * The buffer holds rows for exactly ONE provisional variant at a time; a retain
 * under a different variant clears it first (a variant switch must not let old
 * rows leak into a new dex).
 */
export function createPreContextBuffer() {
  // The provisional variant the currently-buffered rows were read under.
  let bufferedVariant: PokedexVariant | null = null;
  // rowKey -> buffered row. A later read for the same band upgrades the ids.
  const rows = new Map<string, PreContextRow>();

  /** Drop rows last seen more than the TTL before `frameIndex`. Returns count. */
  function expireOlderThan(frameIndex: number): number {
    let dropped = 0;
    for (const [key, row] of Array.from(rows)) {
      if (frameIndex - row.frameIndex > PRE_CONTEXT_TTL_FRAMES) {
        rows.delete(key);
        dropped += 1;
      }
    }
    if (rows.size === 0) bufferedVariant = null;
    return dropped;
  }

  /** Evict the oldest row when the buffer is at capacity. */
  function evictOldestIfFull(): void {
    if (rows.size < PRE_CONTEXT_MAX_ROWS) return;
    let oldestKey: string | null = null;
    let oldestFrame = Infinity;
    for (const [key, row] of Array.from(rows)) {
      if (row.frameIndex < oldestFrame) {
        oldestFrame = row.frameIndex;
        oldestKey = key;
      }
    }
    if (oldestKey != null) rows.delete(oldestKey);
  }

  return {
    /**
     * Retain a frame's pre-context rows. Call ONLY for frames that are NOT yet
     * routed under a locked context but DID read dex numbers under a provisional
     * variant (chip-text / app-state guess). A null variant, or no rows, retains
     * nothing.
     *
     * `rows` is the per-row id list keyed by rowKey, with optional parallel
     * yPcts. Returns whether a row was buffered, plus expiry bookkeeping.
     */
    retain(args: {
      variant: PokedexVariant | null;
      rows: Array<{ rowKey: string; ids: number[]; yPcts?: number[] }>;
      frameIndex: number;
    }): PreContextRetainResult {
      const { variant, rows: incoming, frameIndex } = args;
      const expired = expireOlderThan(frameIndex);

      const usable = incoming.filter((r) => r.ids.length > 0);
      if (variant == null || usable.length === 0) {
        return {
          event: expired > 0 ? "idle" : "idle",
          buffered: rows.size,
          expired,
        };
      }

      // Variant switch: rows under a different provisional variant must never
      // bridge into this one. Clear the buffer and start fresh under `variant`.
      let switched = false;
      if (bufferedVariant != null && bufferedVariant !== variant) {
        rows.clear();
        switched = true;
      }
      bufferedVariant = variant;

      for (const r of usable) {
        evictOldestIfFull();
        const prev = rows.get(r.rowKey);
        if (!prev) {
          rows.set(r.rowKey, {
            rowKey: r.rowKey,
            ids: dedupeInts(r.ids),
            yPcts: alignYPcts(r.ids, r.yPcts),
            frameIndex,
          });
          continue;
        }
        // Upgrade an existing band: union new ids (keeping their yPcts), refresh
        // the frame index so a re-read keeps the row alive.
        for (let i = 0; i < r.ids.length; i += 1) {
          const id = r.ids[i];
          if (!Number.isFinite(id) || id < 1) continue;
          if (!prev.ids.includes(id)) {
            prev.ids.push(id);
            prev.yPcts.push(yPctAt(r.yPcts, i));
          }
        }
        prev.frameIndex = frameIndex;
      }

      return {
        event: switched ? "variant_switch_reset" : "observed",
        buffered: rows.size,
        expired,
      };
    },

    /**
     * Promote buffered rows when a context lock arrives. Returns the rows to
     * re-observe under `lockedVariant`, then CLEARS them (promotion is once-only
     * per buffered set). Rows are promoted ONLY when:
     *   - the buffered variant matches `lockedVariant` (else it is a switch:
     *     clear and promote nothing), and
     *   - they have not aged past the TTL relative to `frameIndex`.
     */
    promote(args: {
      lockedVariant: PokedexVariant;
      frameIndex: number;
    }): PreContextPromoteResult {
      const { lockedVariant, frameIndex } = args;
      const expired = expireOlderThan(frameIndex);

      if (rows.size === 0 || bufferedVariant == null) {
        return { event: "idle", rows: [], expired };
      }

      // The lock names a DIFFERENT variant than the rows were read under: those
      // rows belong to another dex. Drop them; promote nothing.
      if (bufferedVariant !== lockedVariant) {
        rows.clear();
        bufferedVariant = null;
        return { event: "variant_switch_reset", rows: [], expired };
      }

      const promoted: PromotedRow[] = Array.from(rows.values()).map((r) => ({
        rowKey: r.rowKey,
        ids: [...r.ids].sort((a, b) => a - b),
        yPcts: [...r.yPcts],
      }));
      rows.clear();
      bufferedVariant = null;
      return { event: "promoted", rows: promoted, expired };
    },

    /** Distinct rows currently buffered. */
    size(): number {
      return rows.size;
    },

    /** The provisional variant the buffered rows were read under, or null. */
    variant(): PokedexVariant | null {
      return bufferedVariant;
    },

    /** Reset between scan sessions. */
    reset(): void {
      rows.clear();
      bufferedVariant = null;
    },
  };
}

export type PreContextBuffer = ReturnType<typeof createPreContextBuffer>;

// ── helpers ──────────────────────────────────────────────────────────────

function dedupeInts(ids: number[]): number[] {
  const out: number[] = [];
  for (const id of ids) {
    if (!Number.isFinite(id) || id < 1) continue;
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

/** Build a yPct array parallel to the DEDUPED ids in `dedupeInts(ids)`. */
function alignYPcts(ids: number[], yPcts: number[] | undefined): number[] {
  const out: number[] = [];
  const seen = new Set<number>();
  for (let i = 0; i < ids.length; i += 1) {
    const id = ids[i];
    if (!Number.isFinite(id) || id < 1 || seen.has(id)) continue;
    seen.add(id);
    out.push(yPctAt(yPcts, i));
  }
  return out;
}

function yPctAt(yPcts: number[] | undefined, i: number): number {
  const v = yPcts?.[i];
  return typeof v === "number" && Number.isFinite(v) ? v : Number.NaN;
}
